import { CtaList } from '@/app/_components/core/cta-list';
import { EditableMarkup, EditableText } from '@/app/_components/core/editable-markup';
import { Headline } from '@/app/_components/core/headline';
import { RichText } from '@/app/_components/core/rich-text';
import { Tagline } from '@/app/_components/core/tagline';
import { getIntroAlignmentClasses } from '@/app/_components/shared/utils/component-helpers';
import { cn } from '@/app/_lib/utils';
import type { IntroWithAlignment } from '@/entities/models/page';
import type { ElementType } from 'react';

export interface SectionIntroProps {
  intro: IntroWithAlignment;
  isNested?: boolean;
  /** Position first CTA as "more" link on the right (desktop only, left alignment) */
  withMoreLink?: boolean;
  className?: string;
  trackingPrefix?: string;
}

/**
 * SectionIntro - Reusable intro block for section components
 *
 * Handles:
 * - Tagline, Headline, RichText, CTAs rendering
 * - Alignment logic (left vs center)
 * - First CTA absolute positioning on desktop when left-aligned
 * - Nested handling (always left-aligned, no max-width)
 */
export function SectionIntro({
  intro,
  isNested,
  withMoreLink = false,
  className,
  trackingPrefix,
}: SectionIntroProps) {
  const { headline, headlineType, subline, introtext, introAlignment, ctas, inlineEditing } =
    intro;
  const alignmentClasses = getIntroAlignmentClasses(introAlignment);
  const isLeftAligned = introAlignment === 'left';
  const showMoreLink =
    withMoreLink && isLeftAligned && !isNested && !!ctas?.length;
  // .at(0)/slice, not array destructuring: destructuring types index 0 as
  // always present (TS without noUncheckedIndexedAccess), .at() is honestly
  // `| undefined` — the firstCta guard in the JSX below is real.
  const firstCta = ctas?.at(0);
  const remainingCtas = ctas?.slice(1) ?? [];

  // In the visual editor the CMS's markup replaces a field, and an empty
  // field still renders, so it can be filled.
  const headlineEnabled = headlineType && headlineType !== 'disabled';
  const showHeadline = headlineEnabled && [headline, inlineEditing?.headline].some(Boolean);
  const showSubline = [subline, inlineEditing?.subline].some(Boolean);
  const showIntrotext = [introtext, inlineEditing?.introtext].some(Boolean);
  // linkTitle/linkUrl are required strings (ctaSchema) — the .trim() filter
  // guards against EMPTY strings, no optional chaining needed.
  const validCtas = ctas?.filter((cta) => cta.linkTitle.trim() && cta.linkUrl.trim());
  const hasContent =
    (headlineEnabled && headline?.trim()) ||
    [subline?.trim(), introtext?.trim(), inlineEditing].some(Boolean) ||
    !!validCtas?.length;

  if (!hasContent) {
    return null;
  }

  return (
    <div
      className={cn(
        'grid gap-section-title-gap-lg-mobile xl:gap-section-title-gap-lg-desktop',
        !isNested && 'mb-10 lg:mb-12',
        showMoreLink
          ? 'relative lg:pr-52'
          : cn(!isNested && 'max-w-2xl', !isNested && alignmentClasses.container),
        className
      )}
    >
      {showSubline && (
        <Tagline align={isNested ? 'left' : introAlignment} className="mb-0">
          <EditableText markup={inlineEditing?.subline}>{subline}</EditableText>
        </Tagline>
      )}

      {showHeadline && (
        <Headline as={headlineType as ElementType} className="mb-0">
          <EditableText markup={inlineEditing?.headline}>{headline}</EditableText>
        </Headline>
      )}

      {showIntrotext &&
        (inlineEditing?.introtext ? (
          <EditableMarkup html={inlineEditing.introtext} className="max-w-2xl editable-rich-text" />
        ) : (
          <RichText>{introtext ?? ''}</RichText>
        ))}

      {showMoreLink ? (
        <>
          {firstCta && (
            <CtaList
              ctas={[firstCta]}
              className="lg:absolute lg:top-0 lg:right-0 lg:z-10"
              trackingPrefix={trackingPrefix}
            />
          )}
          {remainingCtas.length > 0 && (
            <CtaList ctas={remainingCtas} trackingPrefix={trackingPrefix} />
          )}
        </>
      ) : (
        ctas &&
        ctas.length > 0 && (
          <CtaList
            ctas={ctas}
            className={cn(!isNested && alignmentClasses.buttons)}
            trackingPrefix={trackingPrefix}
          />
        )
      )}
    </div>
  );
}
